import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} PriceBreak
 * @property {number} quantity - Minimum quantity for this price tier
 * @property {string} price - Formatted price string (e.g., "$9.50 USD")
 * @property {number} [cents] - Price in minor units for B2B display
 */

/**
 * @typedef {Object} PricePerItemRefs
 * @property {HTMLElement} [pricePerItemText]
 */

/**
 * @extends {Component<PricePerItemRefs>}
 */
class PricePerItemComponent extends Component {
  /** @type {PriceBreak[]} */
  #priceBreaks = [];
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#parsePriceBreaks();
    this.#attachEventListeners();
    this.#updatePriceDisplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #parsePriceBreaks() {
    const minQuantity = parseInt(this.dataset.minQuantity || '') || 1;
    const { variantPrice, priceBreaks: priceBreaksData, variantCents } = this.dataset;

    if (variantPrice) {
      const cents = variantCents ? parseInt(variantCents, 10) : undefined;
      this.#priceBreaks.push({ quantity: minQuantity, price: variantPrice, cents });
    }

    if (priceBreaksData) {
      const breaks = JSON.parse(priceBreaksData);
      for (const row of breaks) {
        const quantity = row.quantity;
        const price = row.price;
        if (quantity && price) {
          const cents = row.cents != null ? parseInt(String(row.cents), 10) : undefined;
          this.#priceBreaks.push({ quantity: parseInt(String(quantity), 10), price, cents });
        }
      }
    }

    this.#priceBreaks.sort((a, b) => b.quantity - a.quantity);
  }

  #attachEventListeners() {
    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
  }

  #handleQuantityUpdate = (event) => {
    const form = this.closest('product-form-component');
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;
    this.#updatePriceDisplay();
  };

  #handleCartUpdate = () => {
    this.#updatePriceDisplay();
  };

  #getCurrentQuantity() {
    const form = this.closest('product-form-component');
    const quantityInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="quantity"]'));
    if (!quantityInput) return 1;

    const cartQty = parseInt(quantityInput.getAttribute('data-cart-quantity') || '0') || 0;
    const inputQty = parseInt(quantityInput.value) || 1;

    return cartQty + inputQty;
  }

  updatePriceDisplay() {
    if (!this.#priceBreaks.length || !this.refs.pricePerItemText) return;

    const quantity = this.#getCurrentQuantity();

    const priceBreak =
      this.#priceBreaks.find((pb) => quantity >= pb.quantity) ?? this.#priceBreaks[this.#priceBreaks.length - 1];

    if (priceBreak) {
      let cents = priceBreak.cents;
      if (cents == null && this.dataset.variantCents) {
        cents = parseInt(this.dataset.variantCents, 10);
      }
      const centsAttr = cents != null && !Number.isNaN(cents) ? cents : '';

      this.refs.pricePerItemText.innerHTML = `${this.dataset.atText} <span class="b2b-money" data-b2b-cents="${centsAttr}">${priceBreak.price}</span><span class="b2b-moms-suffix" hidden> ekskl. moms</span>/${this.dataset.eachText}`;
      document.dispatchEvent(new CustomEvent('palledesign:b2b-reapply'));
    }
  }

  #updatePriceDisplay = () => {
    this.updatePriceDisplay();
  };
}

if (!customElements.get('price-per-item')) {
  customElements.define('price-per-item', PricePerItemComponent);
}
